/* eslint-disable react/no-unstable-nested-components */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import * as XLSX from "xlsx-js-style";
import {
  ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
  BarChart, Bar,
} from "recharts";
import TopBar from "@/components/TopBar";
import api from "@/lib/api";
import { fmtNum, fmtRiyal, toEnglishDigits } from "@/utils/toEnglishDigits";
import { fmtDate } from "@/utils/date";
import CustomDatePicker from "@/components/DateTimePicker";
import {
  ArrowRight,
  TrendUp,
  TrendDown,
  ChartBar,
  ClipboardText,
  CurrencyCircleDollar,
  CheckCircle,
  XCircle,
  Users,
  Car as CarIcon,
  Buildings,
  MapPin,
  Money,
  Clock,
  MicrosoftExcelLogo,
  Trophy,
} from "@phosphor-icons/react";

const GOLD = "#C9A961";
const NAVY = "#050A15";
const CARD = "#0F1F42";
const CHART_COLORS = ["#C9A961", "#E1C484", "#8A6D2F", "#D4B96B", "#F2E1A4"];
const STATUS_LABELS = {
  pending: "قيد المراجعة",
  confirmed: "مؤكد",
  completed: "مكتمل",
  cancelled: "ملغى",
  rejected: "مرفوض",
};

const PERIODS = [
  { key: "today", label: "اليوم" },
  { key: "week", label: "الأسبوع" },
  { key: "month", label: "الشهر" },
  { key: "3months", label: "3 شهور" },
  { key: "custom", label: "مخصص" },
];

// ─── KPI Card ─────────────────────────────────────────────────────────
function KPICard({ icon: Icon, label, value, change, prev, isPercent, testId, tone = "gold" }) {
  // If there's no previous data at all, don't paint a fake +100% delta.
  const noPrev = !prev;
  const positive = !noPrev && change > 0;
  const negative = !noPrev && change < 0;
  const changeBg = noPrev
    ? "bg-primary/15 border-primary/40 text-primary"
    : positive
      ? "bg-[#22C55E]/15 border-[#22C55E]/40 text-[#22C55E]"
      : negative
        ? "bg-destructive/15 border-destructive/40 text-destructive"
        : "bg-white/5 border-white/15 text-white/60";
  return (
    <div
      className="rounded-2xl bg-card border border-primary/25 p-5 relative overflow-hidden fadeup"
      data-testid={testId}
      style={{ background: "linear-gradient(135deg, rgba(201,169,97,0.05) 0%, transparent 100%)" }}
    >
      <div className={`absolute top-3 left-3 text-[11px] font-black px-2 py-1 rounded-full border ${changeBg}`}>
        {noPrev ? (
          <span>جديد</span>
        ) : (
          <>
            {positive ? <TrendUp size={10} weight="bold" className="inline mr-0.5" /> : negative ? <TrendDown size={10} weight="bold" className="inline mr-0.5" /> : null}
            <span className="num" dir="ltr">{Math.abs(change).toFixed(1)}%</span>
          </>
        )}
      </div>
      <Icon size={26} weight="duotone" className={tone === "red" ? "text-destructive" : tone === "green" ? "text-[#22C55E]" : "text-primary"} />
      <div className="text-white/55 text-xs mt-2">{label}</div>
      <div className="text-white text-3xl font-black num mt-1" dir="ltr">
        {isPercent ? `${fmtNum(value)}%` : fmtNum(value)}
      </div>
      <div className="text-[10px] text-white/35 mt-1">عن الفترة السابقة</div>
    </div>
  );
}

// ─── Ranking Card ─────────────────────────────────────────────────────
function RankingCard({ icon: Icon, title, items, formatItem, emptyMessage, testId }) {
  return (
    <div className="rounded-2xl bg-card border border-primary/20 p-5 fadeup" data-testid={testId}>
      <div className="flex items-center gap-2 text-primary font-bold mb-4 pb-3 border-b border-primary/15">
        <Icon size={18} weight="duotone" />
        <span>{title}</span>
      </div>
      {items.length === 0 ? (
        <div className="py-6 text-center text-white/40 text-sm">{emptyMessage || "لا توجد بيانات"}</div>
      ) : (
        <ol className="space-y-1">
          {items.map((item, i) => (
            <li
              key={i}
              className="flex items-center gap-3 py-2.5 border-b border-primary/10 last:border-0"
            >
              <div
                className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-black num ${
                  i === 0
                    ? "bg-primary text-primary-foreground"
                    : i === 1
                      ? "bg-primary/60 text-primary-foreground"
                      : i === 2
                        ? "bg-primary/35 text-primary-foreground"
                        : "bg-white/10 text-white/60"
                }`}
                dir="ltr"
              >
                {fmtNum(i + 1)}
              </div>
              <div className="flex-1 min-w-0">{formatItem(item)}</div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ─── Heatmap ──────────────────────────────────────────────────────────
function Heatmap({ data }) {
  const [tooltip, setTooltip] = useState(null);
  const days = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];
  const max = useMemo(() => Math.max(1, ...data.map((d) => d.count)), [data]);
  const grid = useMemo(() => {
    const g = Array.from({ length: 7 }, () => Array(24).fill(0));
    data.forEach((cell) => {
      if (cell.day >= 0 && cell.day < 7 && cell.hour >= 0 && cell.hour < 24) {
        g[cell.day][cell.hour] = cell.count;
      }
    });
    return g;
  }, [data]);

  const cellStyle = (v) => {
    if (!v) return { background: "rgba(201,169,97,0.06)" };
    const alpha = 0.15 + (v / max) * 0.85;
    return { background: `rgba(201,169,97,${alpha.toFixed(2)})` };
  };

  return (
    <div className="relative">
      <div className="overflow-x-auto">
        <table className="text-[10px] text-white/50 num" dir="ltr">
          <thead>
            <tr>
              <th className="w-16"></th>
              {Array.from({ length: 24 }, (_, h) => (
                <th key={h} className="text-center pb-1 min-w-[18px]">{h % 3 === 0 ? fmtNum(h) : ""}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {days.map((dayLabel, d) => (
              <tr key={d}>
                <td className="text-right pr-2 text-white/60" dir="rtl">{dayLabel}</td>
                {grid[d].map((v, h) => (
                  <td key={h}>
                    <div
                      data-testid={`heatmap-cell-${d}-${h}`}
                      onMouseEnter={() => setTooltip({ d, h, v, label: dayLabel })}
                      onMouseLeave={() => setTooltip(null)}
                      className="w-full aspect-square rounded-sm transition-all hover:ring-1 hover:ring-primary cursor-help"
                      style={cellStyle(v)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {tooltip && (
        <div className="absolute top-2 left-2 rounded-lg bg-[#0A1936] border border-primary/50 px-3 py-1.5 text-xs" dir="rtl">
          <span className="text-primary font-bold">{tooltip.label}</span> · الساعة{" "}
          <span className="num" dir="ltr">{fmtNum(tooltip.h)}</span> · <span className="text-primary num" dir="ltr">{fmtNum(tooltip.v)}</span> حجز
        </div>
      )}
    </div>
  );
}

// ─── Excel export ─────────────────────────────────────────────────────
function exportExcel(data) {
  const wb = XLSX.utils.book_new();
  const p = data.period;
  const period = `${p.from}_${p.to}`;

  // Summary sheet
  const summary = [
    ["مؤشر", "القيمة"],
    ["الفترة", `${p.from} → ${p.to}`],
    ["إجمالي الحجوزات", toEnglishDigits(String(data.kpis.total_bookings))],
    ["إجمالي الإيرادات (ر.س)", toEnglishDigits(String(data.kpis.total_revenue))],
    ["معدل الاكتمال %", toEnglishDigits(String(data.kpis.completion_rate))],
    ["معدل الإلغاء %", toEnglishDigits(String(data.kpis.cancellation_rate))],
    ["أجرة السائقين (ر.س)", toEnglishDigits(String(data.financial_summary.drivers_wages))],
    ["صافي الربح (ر.س)", toEnglishDigits(String(data.financial_summary.net_profit))],
    ["متوسط قيمة الحجز (ر.س)", toEnglishDigits(String(data.financial_summary.avg_booking_value))],
    ["متوسط رحلات/يوم", toEnglishDigits(String(data.financial_summary.avg_trips_per_day))],
  ];
  const wsSummary = XLSX.utils.aoa_to_sheet(summary);
  wsSummary["!cols"] = [{ wch: 30 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, wsSummary, "الملخص");

  // Top drivers
  const drivers = [["الترتيب", "السائق", "الرحلات", "الإيراد (ر.س)"]];
  data.top_drivers.forEach((d, i) => drivers.push([toEnglishDigits(String(i + 1)), d.name, toEnglishDigits(String(d.trips)), toEnglishDigits(String(d.revenue))]));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(drivers), "أفضل السائقين");

  // Top cars
  const cars = [["الترتيب", "نوع السيارة", "اللوحة", "الرحلات", "الإيراد (ر.س)"]];
  data.top_cars.forEach((c, i) => cars.push([toEnglishDigits(String(i + 1)), c.car_type, c.license_plate, toEnglishDigits(String(c.trips)), toEnglishDigits(String(c.revenue))]));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(cars), "أفضل السيارات");

  // Top companies
  const companies = [["الترتيب", "الشركة", "الحجوزات", "الإجمالي (ر.س)"]];
  data.top_companies.forEach((c, i) => companies.push([toEnglishDigits(String(i + 1)), c.name, toEnglishDigits(String(c.bookings)), toEnglishDigits(String(c.total_spent))]));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(companies), "الشركات");

  // Bookings trend
  const trend = [["التاريخ", "عدد الحجوزات", "الإيراد (ر.س)"]];
  data.bookings_trend.forEach((t) => trend.push([toEnglishDigits(t.date), toEnglishDigits(String(t.count)), toEnglishDigits(String(t.revenue))]));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(trend), "اتجاه الحجوزات");

  XLSX.writeFile(wb, `analytics-${period}.xlsx`);
}

// ─── Main page ────────────────────────────────────────────────────────
export default function AnalyticsPage() {
  const [period, setPeriod] = useState("month");
  const [dfrom, setDfrom] = useState("");
  const [dto, setDto] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === "custom") {
        if (!dfrom || !dto) {
          setLoading(false);
          return;
        }
        params.dfrom = dfrom;
        params.dto = dto;
      }
      const { data: d } = await api.get("/admin/analytics", { params });
      setData(d);
    } catch {
      toast.error("تعذر تحميل تحليلات");
    } finally {
      setLoading(false);
    }
  }, [period, dfrom, dto]);

  useEffect(() => { load(); }, [load]);

  const statusData = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.status_distribution).map(([k, v]) => ({
      name: STATUS_LABELS[k] || k,
      value: v,
    })).filter((x) => x.value > 0);
  }, [data]);

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <TopBar />
      <main className="max-w-7xl mx-auto p-4 md:p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            className="w-9 h-9 rounded-full border border-primary/30 text-primary hover:bg-primary/10 inline-flex items-center justify-center"
            data-testid="analytics-back"
          >
            <ArrowRight size={16} weight="bold" />
          </Link>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white inline-flex items-center gap-2">
              <ChartBar size={26} weight="duotone" className="text-primary" />
              لوحة الأداء التنفيذية
            </h1>
            <p className="text-white/50 text-sm mt-1">
              مؤشرات حية · أفضل المؤدين · تحليل الاتجاهات · تصدير Excel
            </p>
          </div>
        </div>

        {/* Period chips */}
        <div className="flex flex-wrap gap-2 items-center" data-testid="period-chips">
          <span className="text-primary font-bold text-sm ml-2 inline-flex items-center gap-1">
            <Clock size={14} weight="bold" /> الفترة:
          </span>
          {PERIODS.map((p) => {
            const active = period === p.key;
            return (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                data-testid={`period-${p.key}`}
                className={`px-5 py-2 rounded-full text-sm font-bold border transition-all ${
                  active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-primary/5 border-primary/30 text-white hover:bg-primary/15"
                }`}
              >
                {p.label}
              </button>
            );
          })}
          {period === "custom" && (
            <div className="flex items-center gap-2 mr-2">
              <CustomDatePicker value={dfrom} onChange={setDfrom} placeholder="من" testId="period-from" />
              <CustomDatePicker value={dto} onChange={setDto} placeholder="إلى" testId="period-to" minDate={dfrom ? new Date(dfrom) : undefined} />
            </div>
          )}
          {data && (
            <span className="text-xs text-white/50 num mr-auto" dir="ltr">
              {data.period.from} → {data.period.to}
            </span>
          )}
        </div>

        {loading ? (
          <div className="text-center py-16 text-primary animate-pulse">جارٍ التحميل…</div>
        ) : !data ? (
          <div className="text-center py-16 text-white/50 bg-card border border-primary/15 rounded-2xl">
            اختر الفترة لعرض البيانات
          </div>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kpis-grid">
              <KPICard
                icon={ClipboardText}
                label="عدد الحجوزات"
                value={data.kpis.total_bookings}
                change={data.kpis.total_bookings_change}
                prev={data.kpis.total_bookings_prev}
                testId="kpi-bookings"
              />
              <KPICard
                icon={CurrencyCircleDollar}
                label="الإيرادات (ر.س)"
                value={data.kpis.total_revenue}
                change={data.kpis.total_revenue_change}
                prev={data.kpis.total_revenue_prev}
                testId="kpi-revenue"
              />
              <KPICard
                icon={CheckCircle}
                label="معدل الاكتمال"
                value={data.kpis.completion_rate}
                change={data.kpis.completion_rate_change}
                prev={data.kpis.completion_rate_prev}
                isPercent
                tone="green"
                testId="kpi-completion"
              />
              <KPICard
                icon={XCircle}
                label="معدل الإلغاء"
                value={data.kpis.cancellation_rate}
                change={data.kpis.cancellation_rate_change}
                prev={data.kpis.cancellation_rate_prev}
                isPercent
                tone="red"
                testId="kpi-cancellation"
              />
            </div>

            {/* Rankings 2x2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <RankingCard
                icon={Trophy}
                title="أفضل السائقين"
                items={data.top_drivers}
                testId="rank-drivers"
                emptyMessage="لا توجد رحلات مسندة"
                formatItem={(d) => (
                  <>
                    <div className="text-white font-bold text-sm truncate">{d.name}
                      {d.external && <span className="mr-1 text-[9px] px-1.5 py-0.5 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/50 text-[#F59E0B]">خارجي</span>}
                    </div>
                    <div className="text-white/60 text-xs mt-0.5 num" dir="ltr">
                      {fmtNum(d.trips)} رحلة · <span className="text-primary">{fmtRiyal(d.revenue)}</span>
                    </div>
                  </>
                )}
              />
              <RankingCard
                icon={CarIcon}
                title="أفضل السيارات"
                items={data.top_cars}
                testId="rank-cars"
                emptyMessage="لا توجد بيانات لوحات"
                formatItem={(c) => (
                  <>
                    <div className="text-white font-bold text-sm">{c.car_type}</div>
                    <div className="text-white/60 text-xs mt-0.5 num" dir="ltr">
                      <span className="text-primary/80">{c.license_plate}</span> · {fmtNum(c.trips)} رحلة · <span className="text-primary">{fmtRiyal(c.revenue)}</span>
                    </div>
                  </>
                )}
              />
              <RankingCard
                icon={Buildings}
                title="أفضل الشركات"
                items={data.top_companies}
                testId="rank-companies"
                formatItem={(c) => (
                  <>
                    <div className="text-white font-bold text-sm truncate">{c.name}</div>
                    <div className="text-white/60 text-xs mt-0.5 num" dir="ltr">
                      {fmtNum(c.bookings)} حجز · <span className="text-primary">{fmtRiyal(c.total_spent)}</span>
                    </div>
                  </>
                )}
              />
              <RankingCard
                icon={MapPin}
                title="أشهر المسارات"
                items={data.top_routes}
                testId="rank-routes"
                formatItem={(r) => (
                  <>
                    <div className="text-white font-bold text-sm truncate">
                      {r.pickup} <span className="text-white/40">←</span> {r.destination}
                    </div>
                    <div className="text-white/60 text-xs mt-0.5 num" dir="ltr">
                      {fmtNum(r.count)} رحلة · <span className="text-primary">{fmtRiyal(r.revenue)}</span>
                    </div>
                  </>
                )}
              />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Line: bookings trend */}
              <div className="rounded-2xl bg-card border border-primary/20 p-5 fadeup" data-testid="chart-trend">
                <div className="flex items-center gap-2 text-primary font-bold mb-4">
                  <TrendUp size={18} weight="duotone" />
                  <span>اتجاه الحجوزات اليومي</span>
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={data.bookings_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(201,169,97,0.15)" />
                    <XAxis dataKey="date" tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: NAVY, border: `1px solid ${GOLD}`, borderRadius: 8 }}
                      labelStyle={{ color: GOLD }}
                      itemStyle={{ color: "#fff" }}
                    />
                    <Line type="monotone" dataKey="count" stroke={GOLD} strokeWidth={2.5} dot={{ r: 3, fill: GOLD }} activeDot={{ r: 5 }} name="حجوزات" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Pie: status distribution */}
              <div className="rounded-2xl bg-card border border-primary/20 p-5 fadeup" data-testid="chart-status">
                <div className="flex items-center gap-2 text-primary font-bold mb-4">
                  <CheckCircle size={18} weight="duotone" />
                  <span>توزيع الحالات</span>
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.name} (${e.value})`}>
                      {statusData.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: NAVY, border: `1px solid ${GOLD}`, borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 11, color: "#fff" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Bar: by car type */}
              <div className="rounded-2xl bg-card border border-primary/20 p-5 fadeup" data-testid="chart-cars">
                <div className="flex items-center gap-2 text-primary font-bold mb-4">
                  <CarIcon size={18} weight="duotone" />
                  <span>الحجوزات حسب نوع السيارة</span>
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.bookings_by_car_type} layout="vertical" margin={{ top: 0, right: 10, left: 60, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(201,169,97,0.15)" />
                    <XAxis type="number" tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }} allowDecimals={false} />
                    <YAxis dataKey="car_type" type="category" tick={{ fill: "rgba(255,255,255,0.7)", fontSize: 11 }} width={100} />
                    <Tooltip contentStyle={{ background: NAVY, border: `1px solid ${GOLD}`, borderRadius: 8 }} />
                    <Bar dataKey="count" fill={GOLD} radius={[0, 6, 6, 0]} name="حجوزات" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Heatmap: peak hours */}
              <div className="rounded-2xl bg-card border border-primary/20 p-5 fadeup" data-testid="chart-heatmap">
                <div className="flex items-center gap-2 text-primary font-bold mb-4">
                  <Clock size={18} weight="duotone" />
                  <span>ساعات الذروة (يوم × ساعة)</span>
                </div>
                <Heatmap data={data.peak_hours} />
              </div>
            </div>

            {/* Financial summary */}
            <div
              className="rounded-2xl p-6 border-2 border-primary/50 fadeup"
              data-testid="financial-summary"
              style={{ background: "linear-gradient(135deg, rgba(201,169,97,0.15) 0%, transparent 60%)" }}
            >
              <div className="flex items-center gap-2 text-primary font-bold mb-4">
                <Money size={20} weight="duotone" />
                <span>الملخص المالي</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                {[
                  ["إجمالي الإيرادات", data.financial_summary.total_revenue, "text-primary"],
                  ["أجرة السائقين", data.financial_summary.drivers_wages, "text-destructive"],
                  ["صافي الربح ✨", data.financial_summary.net_profit, "text-[#22C55E]"],
                  ["متوسط الحجز", data.financial_summary.avg_booking_value, "text-white"],
                  ["متوسط/يوم", data.financial_summary.avg_trips_per_day, "text-white"],
                ].map(([label, val, colorClass], i) => (
                  <div key={i} className="text-center">
                    <div className="text-white/50 text-xs">{label}</div>
                    <div className={`text-2xl font-black num mt-1 ${colorClass}`} dir="ltr">
                      {fmtNum(val)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Export */}
            <div className="flex justify-end">
              <button
                onClick={() => {
                  try { exportExcel(data); toast.success("تم تصدير التقرير"); }
                  catch { toast.error("تعذر التصدير"); }
                }}
                data-testid="export-excel"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold"
              >
                <MicrosoftExcelLogo size={18} weight="bold" /> تصدير Excel كامل
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
